import { useState, useEffect, Activity } from 'react';
import copy from 'copy-to-clipboard';

import IconDown from '../icons/down';
import IconLoader from '../icons/loader';
import IconClose from '../icons/close';
import IconCopy from '../icons/copy';
import IconSelected from '../icons/selected';
import { Account as AccountItem } from '../transactions-sidepanels/types';
import ClickOutsideDetector from '../autocomplete/click-outside-detector';
import usePrevious from '../../hooks/use-previous';
import {
  AccountSelectorProps,
  Category,
  Group,
  PrimaryAccount,
  AccountSelectorSize,
  AccountSelectorPosition,
} from './types';
import CategoriesList from './categories-list';
import GroupsList from './groups-list';
import PrimaryAccountsList from './primary-accounts-list';
import AccountsList from './accounts-list';

import styles from './accounts-selector.module.css';
import ButtonPrimary from '../button/button-primary';
import { ButtonSize } from '../button/types';

let timeout: NodeJS.Timeout;

export default function AccountSelector({
  className,
  accounts,
  fullWidth,
  placeholder,
  isLoading,
  accountsIsLoading,
  accountsList,
  setSelectedAccount,
  selectedAccount,
  setAccounts,
  size,
  title,
  onSearch,
  error,
  disabled,
  position,
  setAccountQuery,
  tabIndex,
  transactionAccountSelector,
  multiple,
  setSelectedAccountsList,
  selectedAccountsList,
  primaryAccountsSelector,
  selectedPrimaryAccountsList,
  setSelectedPrimaryAccounts,
}: AccountSelectorProps) {
  const [toggledOn, toggle] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [account, setAccount] = useState<AccountItem>();
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<Category>();
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<Group>();
  const [selectedPrimaryAccountId, setSelectedPrimaryAccountId] = useState('');
  const [selectedPrimaryAccount, setSelectedPrimaryAccount] =
    useState<PrimaryAccount>();
  const [showAccountsList, setShowAccountsList] = useState(false);
  const [isCopied, setIsCopied] = useState(false);
  const [selectedAccounts, setSelectedAccounts] = useState<AccountItem[]>([]);

  const prevSearchText = usePrevious(searchText);
  const prevSelectedAccount = usePrevious(selectedAccount);
  const prevSelectedAccounts = usePrevious(selectedAccounts);
  const prevToggled = usePrevious(toggledOn);

  let content;

  const onClear = () => {
    setAccount(undefined);
    setSelectedAccount('');
    setSearchText('');
    setShowAccountsList(false);
    setSelectedPrimaryAccount(undefined);
    setSelectedPrimaryAccountId('');
    setAccounts([]);
    setSelectedGroup(undefined);
    setSelectedGroupId('');
    setSelectedCategory(undefined);
    setSelectedCategoryId('');
  };

  if (isLoading) {
    content = (
      <li className={styles.loaderWrapper}>
        <IconLoader className={styles.loader} />
      </li>
    );
  } else if (!isLoading && accounts) {
    if (showAccountsList || selectedPrimaryAccount) {
      content = (
        <AccountsList
          selectedCategoryNumber={selectedCategory?.number}
          selectedGroupNumber={selectedGroup?.number}
          selectedPrimaryAccount={selectedPrimaryAccount}
          setSelectedCategoryId={setSelectedCategoryId}
          setSelectedGroupId={setSelectedGroupId}
          setSelectedPrimaryAccountId={setSelectedPrimaryAccountId}
          accountsIsLoading={accountsIsLoading}
          accounts={accountsList}
          toggle={toggle}
          setAccount={setAccount}
          account={account}
          setSelectedAccount={setSelectedAccount}
          selectedAccounts={selectedAccounts}
          setSelectedAccounts={setSelectedAccounts}
          onBack={() => {
            setShowAccountsList(false);

            if (selectedPrimaryAccount) {
              setSelectedPrimaryAccount(undefined);
              setSelectedPrimaryAccountId('');
              setAccounts([]);
            }
          }}
          multiple={multiple}
        />
      );
    } else if (selectedGroup && !selectedPrimaryAccountId) {
      content = (
        <PrimaryAccountsList
          setSelectedPrimaryAccountId={setSelectedPrimaryAccountId}
          selectedGroup={selectedGroup}
          selectedCategoryNumber={selectedCategory?.number}
          selectedCategoryId={selectedCategory?.id}
          setAccountQuery={setAccountQuery}
          onBack={() => {
            setSelectedGroup(undefined);
            setSelectedGroupId('');
          }}
          primaryAccountsSelector={primaryAccountsSelector}
          setSelectedPrimaryAccounts={setSelectedPrimaryAccounts}
          selectedPrimaryAccountsList={selectedPrimaryAccountsList}
        />
      );
    } else if (selectedCategory && !selectedGroupId) {
      content = (
        <GroupsList
          setSelectedGroupId={setSelectedGroupId}
          selectedCategory={selectedCategory}
          onBack={() => {
            setSelectedCategory(undefined);
            setSelectedCategoryId('');
          }}
          primaryAccountsSelector={primaryAccountsSelector}
          setSelectedPrimaryAccounts={setSelectedPrimaryAccounts}
          selectedPrimaryAccountsList={selectedPrimaryAccountsList}
        />
      );
    } else {
      content = (
        <CategoriesList
          accounts={accounts}
          setSelectedCategoryId={setSelectedCategoryId}
          primaryAccountsSelector={primaryAccountsSelector}
          setSelectedPrimaryAccounts={setSelectedPrimaryAccounts}
          selectedPrimaryAccountsList={selectedPrimaryAccountsList}
        />
      );
    }
  }

  useEffect(() => {
    if (
      multiple &&
      !selectedAccounts?.length &&
      selectedAccountsList &&
      selectedAccountsList?.length &&
      (!prevSelectedAccounts || !prevSelectedAccounts.length)
    ) {
      setSelectedAccounts(selectedAccountsList);
    }

    if (
      multiple &&
      !toggledOn &&
      selectedAccounts?.length &&
      !selectedAccountsList?.length
    ) {
      setSelectedAccounts([]);
    }
  }, [
    multiple,
    selectedAccounts,
    selectedAccountsList,
    prevSelectedAccounts,
    toggledOn,
  ]);

  useEffect(() => {
    if (multiple && !toggledOn && prevToggled && searchText) {
      onClear();
    }
  }, [multiple, toggledOn, prevToggled, searchText]);

  useEffect(() => {
    if (selectedCategoryId) {
      const currentCategory = accounts?.find(
        (item) => item.id === selectedCategoryId,
      );

      if (currentCategory) {
        setSelectedCategory(currentCategory);
      }
    }
  }, [selectedCategoryId, accounts]);

  useEffect(() => {
    if (selectedGroupId && selectedCategory) {
      const currentGroup = selectedCategory?.accountGroups?.find(
        (item) => item.id === selectedGroupId,
      );

      if (currentGroup) {
        setSelectedGroup(currentGroup);
      }
    }
  }, [selectedGroupId, selectedCategory]);

  useEffect(() => {
    if (selectedPrimaryAccountId && selectedGroup) {
      const currentPrimaryAccount = selectedGroup?.primaryAccounts?.find(
        (item) => item.id === selectedPrimaryAccountId,
      );

      if (currentPrimaryAccount) {
        setSelectedPrimaryAccount(currentPrimaryAccount);
      }
    }
  }, [selectedPrimaryAccountId, selectedGroup]);

  useEffect(() => {
    if (!selectedCategory && selectedAccount && accounts && !prevSearchText) {
      setAccountQuery(`search=${selectedAccount}`);

      if (accountsList && accountsList.length) {
        const currentAccount = accountsList.find(
          (item) => item.fullNumber === selectedAccount,
        );

        if (currentAccount) {
          if (!multiple) {
            setAccount(currentAccount);
            setSelectedCategoryId(currentAccount.categoryId);
            setSelectedGroupId(currentAccount.groupId);
            setSelectedPrimaryAccountId(currentAccount.primaryId);
          }

          if (currentAccount?.categoryId) {
            setAccountQuery(
              `categoryId=${currentAccount.categoryId}&groupId=${currentAccount.groupId}&primaryId=${currentAccount.primaryId}`,
            );
          }
        }
      }
    }
  }, [
    selectedAccount,
    accounts,
    selectedCategory,
    accountsList,
    prevSearchText,
    setAccountQuery,
    prevSelectedAccount,
    multiple,
  ]);

  useEffect(() => {
    if (prevSelectedAccount !== selectedAccount && accounts) {
      setAccountQuery(`search=${selectedAccount}`);

      if (accountsList && accountsList.length) {
        const currentAccount = accountsList.find(
          (item) => item.fullNumber === selectedAccount,
        );

        if (currentAccount) {
          if (!multiple) {
            setAccount(currentAccount);
            setSelectedCategoryId(currentAccount.categoryId);
            setSelectedGroupId(currentAccount.groupId);
            setSelectedPrimaryAccountId(currentAccount.primaryId);
          }

          if (currentAccount.categoryId) {
            setAccountQuery(
              `categoryId=${currentAccount.categoryId}&groupId=${currentAccount.groupId}&primaryId=${currentAccount.primaryId}`,
            );
          }
        }
      }
    }
  }, [
    selectedAccount,
    prevSelectedAccount,
    accounts,
    accountsList,
    setAccountQuery,
    multiple,
  ]);

  const onCopy = (e: React.SyntheticEvent) => {
    e.stopPropagation();

    if (account) {
      setIsCopied(true);
      copy(account.fullNumber);
    }
  };

  useEffect(() => {
    if (prevSearchText && !searchText) {
      setAccount(undefined);
      setSelectedCategory(undefined);
      setSelectedGroup(undefined);
      setSelectedPrimaryAccount(undefined);
      setSelectedAccount('');
      setSelectedCategoryId('');
      setSelectedGroupId('');
      setSelectedPrimaryAccountId('');
      setAccounts([]);
    }
  }, [prevSearchText, searchText, setAccounts, setSelectedAccount]);

  useEffect(() => {
    if (searchText && !selectedAccount && account) {
      onClear();
    }
  }, [selectedAccount, account, searchText, prevSelectedAccounts]);

  useEffect(() => {
    if (account) {
      setSearchText(account.description);
    }
  }, [account]);

  useEffect(() => {
    if (isCopied) {
      setTimeout(() => {
        setIsCopied(false);
      }, 1200);
    }
  }, [isCopied]);

  return (
    <ClickOutsideDetector setOptionsListVisible={toggle}>
      <>
        <div
          className={`${styles.accountSelectorWrapper} ${className} ${
            fullWidth ? styles.accountSelectorWrapperFullWidth : ''
          } ${size === AccountSelectorSize.SMALL ? styles.smallSize : ''} ${
            title ? styles.withTitle : ''
          }`}
        >
          {title && <div className={styles.accountSelectorTitle}>{title}</div>}
          <div className={styles.accountSelectorInputWrapper}>
            {!primaryAccountsSelector && (
              <input
                type="text"
                className={`${styles.accountSelectorInput} ${
                  account ? styles.morePadding : ''
                } ${disabled ? styles.disabled : ''} ${
                  error ? styles.error : ''
                }`}
                tabIndex={tabIndex}
                placeholder={placeholder}
                onFocus={() => {
                  toggle(true);
                }}
                onChange={(e) => {
                  clearTimeout(timeout);

                  setSearchText(e.target.value);

                  if (onSearch && e.target.value) {
                    timeout = setTimeout(() => {
                      setShowAccountsList(true);
                      onSearch(e.target.value);
                    }, 400);
                  }

                  if (!e.target.value.length) {
                    setShowAccountsList(false);
                    setAccounts([]);
                  }
                }}
                value={searchText ?? ''}
                onKeyDown={(e) => e.key === 'Tab' && toggle(false)}
                disabled={disabled}
                autoComplete="off"
              />
            )}
            {primaryAccountsSelector && (
              <button
                type="button"
                className={`${styles.accountSelectorInput} ${styles.accountSelectorButton} ${
                  error ? styles.error : ''
                }`}
                onClick={() => toggle(!toggledOn)}
              >
                {placeholder}
              </button>
            )}
            {!disabled && selectedAccount && (
              <button
                type="button"
                className={`${styles.iconButton} ${styles.iconButtonClear}`}
                onClick={onClear}
                tabIndex={-1}
                aria-label="Close"
              >
                <IconClose className={styles.icon} />
              </button>
            )}
            {!transactionAccountSelector && selectedAccount && (
              <button
                type="button"
                className={`${styles.iconButton} ${styles.iconButtonCopy} ${
                  disabled ? styles.lessRight : ''
                }`}
                onClick={onCopy}
                tabIndex={-1}
              >
                {isCopied ? (
                  <IconSelected className={styles.icon} />
                ) : (
                  <IconCopy className={styles.icon} />
                )}
              </button>
            )}

            {account && (
              <div
                className={`${styles.suffix} ${
                  disabled ? styles.lessRight : ''
                } ${transactionAccountSelector ? styles.noRight : ''} ${
                  transactionAccountSelector && !disabled
                    ? styles.smallRight
                    : ''
                }`}
              >
                {account.fullNumber}
              </div>
            )}

            <button
              type="button"
              className={styles.iconButton}
              onClick={(e) => {
                e.stopPropagation();
                toggle((prev) => !prev);
              }}
              disabled={disabled}
              tabIndex={-1}
              aria-label="Toggle"
            >
              <IconDown
                className={`${styles.iconArrow} ${
                  toggledOn ? styles.rotate : ''
                } ${primaryAccountsSelector ? styles.smallSizeIcon : ''}`}
              />
            </button>
          </div>
          <Activity mode={toggledOn ? 'visible' : 'hidden'}>
            <ul
              className={`${styles.accountsList} ${
                position === AccountSelectorPosition.TOP
                  ? styles.accountsListTop
                  : styles.accountsListBottom
              }`}
            >
              {content}
              {multiple && (
                <div className={styles.accountsListFooter}>
                  <ButtonPrimary
                    size={ButtonSize.SMALL}
                    text="Apply"
                    onClick={() => {
                      if (setSelectedAccountsList) {
                        setSelectedAccountsList(selectedAccounts);
                      }

                      onClear();
                      toggle(false);
                    }}
                  />
                </div>
              )}
            </ul>
          </Activity>
        </div>
        {error && <div className={styles.errorMessage}>{error}</div>}
      </>
    </ClickOutsideDetector>
  );
}
